import React, { FC, useLayoutEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Category } from 'shared/types/product/Category';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import MarketButton from 'components/commercetools-ui/organisms/market-button/market-button';
import { default as Account } from 'components/icons/account';
import { default as Help } from 'components/icons/icon-help';
import { default as Wishlist } from 'components/icons/wishlist';
import { useFormat } from 'helpers/hooks/useFormat';
import { useAccount } from 'frontastic';
import MobileMenuNavButton from '../atoms/menu-nav-button';
import Button from 'components/commercetools-ui/atoms/button';

export interface Props {
  showMenu: boolean;
  insertCategory: (category: Category) => void;
  hideHeaderMenu: () => void;
}

interface HeaderLink {
  id: string;
  text: string;
  target?: string;
  link: string;
  className?: string;
  icon?: string;
  Component?: React.ReactElement;
}

const MobileMenuFooter: FC<Props> = ({ showMenu, hideHeaderMenu, insertCategory }) => {
  const router = useRouter();
  const { formatMessage } = useFormat({ name: 'common' });
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const marketButtonRef = useRef<HTMLDivElement>(null);
  const { account, logout } = useAccount();
  const [_, setLanguageMenuTop] = useState(false);

  const tabs = [
    { name: formatAccountMessage({ id: 'my.account', defaultMessage: 'My Account' }), href: '/account#' },
    { name: formatAccountMessage({ id: 'orders', defaultMessage: 'Orders' }), href: '/account#orders' },
    {
      name: formatAccountMessage({ id: 'payment.methods', defaultMessage: 'Payment methods' }),
      href: '/account#payment',
    },
    { name: formatAccountMessage({ id: 'addresses', defaultMessage: 'Addresses' }), href: '/account#addresses' },
    {
      name: formatAccountMessage({ id: 'customer.support', defaultMessage: 'Customer support' }),
      href: '/account#support',
    },
  ];

  const headerLinks: HeaderLink[] = [
    {
      id: 'visit.myamericangolf',
      text: 'Personalised Golf Products',
      link: 'https://myamericangolf.co.uk/',
      target: '_blank',
      className: '',
      Component: <i className="icon icon-loader"></i>,
    },
    // {
    //   id: 'visit.myamericangolf',
    //   text: 'Visit MyAmericanGolf',
    //   link: 'https://myamericangolf.co.uk/',
    //   target: '_blank',
    //   className: '',
    // },
    {
      id: 'store.locator',
      text: 'Store Locator',
      link: '/find-stores',
      className: '',
      Component: <i className="icon icon-location"></i>,
    },
    {
      id: 'help.info',
      text: 'Help & Info',
      link: '/services',
      Component: <i className="icon icon-info"></i>,
    },
    {
      id: 'account.info',
      text: 'My Account/Register',
      link: account ? '/account' : '/login',
      Component: <Account className="h-16 w-16" />,
    },
    {
      id: 'Wishlist',
      text: 'wishlist',
      link: account ? '/wishlist' : '/login',
      Component: <Wishlist className="h-16 w-16" totalWishlistItems={0} />,
    },
  ];

  const accountButton: Category = {
    categoryId: 'myAccount',
    name: formatAccountMessage({ id: 'my.account', defaultMessage: 'My Account' }),
    depth: 1,
    subCategories: tabs.map((tab) => {
      return { categoryId: tab.href, name: tab.name, slug: tab.href, subCategories: [], depth: 1 };
    }),
  };

  const handleLogout = () => {
    logout().then(() => router.push('/login'));
    hideHeaderMenu();
  };

  useLayoutEffect(() => {
    if (marketButtonRef.current) {
      if (window.innerHeight - marketButtonRef.current.getBoundingClientRect().bottom < 100) setLanguageMenuTop(true);
      else setLanguageMenuTop(false);
    }
  }, [showMenu]);

  return (
    <div className="bg-[#ededed]">
      <div className="mb-16 w-full border border-neutral-400 " />
      <a href="/club-page/2019-club-out.html" className="ml-25 font-semibold">
        {formatMessage({ id: 'AG.CLUB', defaultMessage: 'AG CLUB' })}
      </a>
      <p className="font-400 ml-25 mt-20">
        {formatMessage({ id: 'Personalised.Products', defaultMessage: 'Personalised Golf Products' })}
      </p>

      {account ? (
        <div className="ml-24 mr-22 block pt-16 md:hidden">
          <MobileMenuNavButton
            link={accountButton}
            onClick={() => insertCategory(accountButton)}
            hideHeaderMenu={hideHeaderMenu}
          />
          <Button variant="ghost" className="py-2" size="fit" onClick={handleLogout}>
            <Typography className="text-16 font-medium text-primary-black">
              {formatAccountMessage({ id: 'sign.out', defaultMessage: 'Sign out' })}
            </Typography>
          </Button>
        </div>
      ) : (
        <div className="ml-24 mr-22 block pt-16 md:hidden">
          <Link link="/login" onClick={hideHeaderMenu} className="px-0">
            <Typography className="text-16 font-medium text-primary-black">
              {formatAccountMessage({ id: 'sign.in', defaultMessage: 'Sign in' })}
            </Typography>
          </Link>
        </div>
      )}

      {/* <div className="mx-24 flex py-15">
        {account ? (
          <Button variant="ghost" size="fit" onClick={handleLogout}>
            <Typography className="text-16 font-normal text-primary-black">
              {formatAccountMessage({ id: 'sign.out', defaultMessage: 'Sign out' })}
            </Typography>
          </Button>
        ) : (
          <Link link="/login" onClick={hideHeaderMenu} className="px-0">
            <Typography className="text-16 font-normal text-primary-black">
              {formatAccountMessage({ id: 'sign.in', defaultMessage: 'Sign in' })}
            </Typography>
          </Link>
        )}
      </div> */}

      <div className="mx-24 flex flex-col gap-12 py-16">
        {headerLinks.map(({ id, text, target, link, Component }) => (
          <Link target={target} key={id} link={link} className="flex items-center gap-5 py-10">
            {Component}
            <span className={`font-20 font-400 text-16 text-black`}>{text}</span>
          </Link>
        ))}
        <div className="flex items-center gap-5 py-5">
          <MarketButton onClickHandler={hideHeaderMenu} />
        </div>
      </div>
    </div>
  );
};

export default MobileMenuFooter;
